// The chat's goals, and the earlier goals it could continue toward, through
// the sanctioned port seam (`conversationGoals`: one
// pure server read, never a command), measured over the chat's materials.
// The Kontext home's "Mål" group and every material's "Når det målen?" lens
// project from this ONE read, so they never disagree.
//
// It re-reads whenever `worldKey` changes (the host passes the library value,
// re-read after every turn, so goals or links the agent wrote in a turn show
// live), when the chat or its materials change, and after the author adds or
// removes a goal here (`reload`). A read in flight keeps the previous value; a
// failed read keeps it too (the views never guess). Cancellation-guarded: the
// last key wins.

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import type { AppPorts } from "../../ports.js";
import { type ChatGoalsRead, emptyGoalsRead } from "./goal.js";

export const useConversationGoals = (
    conversationGoals: AppPorts["conversationGoals"],
    conversationId: string | null,
    materialIds: readonly string[],
    worldKey: unknown,
    // The transcript's length: a turn that only wrote goal links (no library
    // change) still re-reads when its steps and reply land.
    turnKey: number,
): ChatGoalsRead & { readonly reload: () => void } => {
    const [read, setRead] = useState<ChatGoalsRead>(emptyGoalsRead);
    const [tick, setTick] = useState(0);
    const key = materialIds.join("\n");
    // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for `materialIds` (a fresh array each render); `worldKey`, `turnKey` and `tick` ARE the re-read signals.
    useEffect(() => {
        if (conversationId === null) {
            setRead(emptyGoalsRead);
            return;
        }
        let cancelled = false;
        conversationGoals(conversationId, materialIds).then((r) => {
            if (!cancelled && Result.isOk(r)) setRead(r.value);
        });
        return () => {
            cancelled = true;
        };
    }, [conversationGoals, conversationId, key, worldKey, turnKey, tick]);
    return { ...read, reload: () => setTick((n) => n + 1) };
};
